import { enumMembers, type ApiSchema } from "../apiSchema";
import { jsonBody, requestJson, withQuery } from "../http/httpClient";
import { readArray, readInteger, readNullableString, readObject, readOneOf, readString, readStringRecord } from "../http/jsonDecoding";

/**
 * Comments and issues on any item (FR-VERSION-6, `docs/components/reviews.md` R-7). A
 * subject names what is discussed by reference, so a comment on a class shows on the class
 * and in every review that changes it.
 */

export type SubjectKind = ApiSchema<"SubjectKind">;
export type IssueState = ApiSchema<"IssueState">;

const SUBJECT_KINDS = enumMembers<SubjectKind>({
  change_set: true,
  item: true,
  ontology_term: true,
  thread_entry: true,
  collection_version: true,
});
const ISSUE_STATES = enumMembers<IssueState>({ open: true, resolved: true });

export interface DiscussionSubject {
  readonly kind: SubjectKind;
  /** The reference in its text form: an ID, an IRI, `<thread>#<n>` or `<collection>@<commit>`. */
  readonly value: string;
}

export interface Comment {
  readonly commentId: string;
  readonly subject: DiscussionSubject;
  readonly issueId: string | null;
  readonly changeSetId: string | null;
  readonly author: string;
  readonly at: string;
  readonly body: string;
}

export interface Issue {
  readonly issueId: string;
  readonly subject: DiscussionSubject;
  readonly title: string;
  readonly state: IssueState;
  readonly revision: number;
  readonly openedBy: string;
  readonly openedAt: string;
}

export interface Discussion {
  readonly comments: readonly Comment[];
  readonly issues: readonly Issue[];
  /** Each named person's display name by user ID, members or not, as the server read them (W-08c). */
  readonly names: ReadonlyMap<string, string>;
}

function readSubject(object: Readonly<Record<string, unknown>>): DiscussionSubject {
  return { kind: readOneOf(object, "subject_kind", SUBJECT_KINDS), value: readString(object, "subject") };
}

function readComment(value: unknown): Comment {
  const comment = readObject(value, "comment");
  return {
    commentId: readString(comment, "comment_id"),
    subject: readSubject(comment),
    issueId: readNullableString(comment, "issue_id"),
    changeSetId: readNullableString(comment, "change_set_id"),
    author: readString(comment, "author"),
    at: readString(comment, "at"),
    body: readString(comment, "body"),
  };
}

function readIssue(value: unknown): Issue {
  const issue = readObject(value, "issue");
  return {
    issueId: readString(issue, "issue_id"),
    subject: readSubject(issue),
    title: readString(issue, "title"),
    state: readOneOf(issue, "state", ISSUE_STATES),
    revision: readInteger(issue, "revision"),
    openedBy: readString(issue, "opened_by"),
    openedAt: readString(issue, "opened_at"),
  };
}

function readDiscussion(value: unknown): Discussion {
  const body = readObject(value, "discussion");
  return {
    comments: readArray(body["comments"], "comments", readComment),
    issues: readArray(body["issues"], "issues", readIssue),
    names: new Map(Object.entries(readStringRecord(body, "names"))),
  };
}

const projectPath = (projectId: string): `/${string}` => `/projects/${encodeURIComponent(projectId)}`;

export function getDiscussion(projectId: string, subject: DiscussionSubject, signal?: AbortSignal): Promise<Discussion> {
  return requestJson({
    method: "GET",
    path: withQuery(`${projectPath(projectId)}/discussion`, { subject_kind: subject.kind, subject: subject.value }),
    signal,
    decodeResponse: readDiscussion,
  });
}

/** The project's issues, oldest first (B's `GET /projects/{id}/issues`): on the DMW project, the problem reports. */
export function listIssues(projectId: string, signal?: AbortSignal): Promise<readonly Issue[]> {
  return requestJson({
    method: "GET",
    path: `${projectPath(projectId)}/issues`,
    signal,
    decodeResponse: (body) => readArray(body, "issues", readIssue),
  });
}

/** A review's discussion: on the change set, its items and terms, and its origin. */
export function getReviewDiscussion(changeSetId: string, signal?: AbortSignal): Promise<Discussion> {
  return requestJson({
    method: "GET",
    path: `/content/change-sets/${encodeURIComponent(changeSetId)}/discussion`,
    signal,
    decodeResponse: readDiscussion,
  });
}

export function addComment(
  projectId: string,
  comment: {
    readonly subject: DiscussionSubject;
    readonly body: string;
    readonly issueId: string | null;
    readonly changeSetId: string | null;
  },
): Promise<Comment> {
  return requestJson({
    method: "POST",
    path: `${projectPath(projectId)}/comments`,
    body: jsonBody({
      subject_kind: comment.subject.kind,
      subject: comment.subject.value,
      body: comment.body,
      issue_id: comment.issueId,
      change_set_id: comment.changeSetId,
    }),
    decodeResponse: readComment,
  });
}

export function openIssue(
  projectId: string,
  issue: {
    readonly subject: DiscussionSubject;
    readonly title: string;
    readonly body: string;
    readonly changeSetId: string | null;
  },
): Promise<Issue> {
  return requestJson({
    method: "POST",
    path: `${projectPath(projectId)}/issues`,
    body: jsonBody({
      subject_kind: issue.subject.kind,
      subject: issue.subject.value,
      title: issue.title,
      body: issue.body,
      change_set_id: issue.changeSetId,
    }),
    decodeResponse: (body) => readIssue(readObject(body, "opened issue")["issue"]),
  });
}

/** Resolve or reopen an issue on the revision the user read. */
export function changeIssue(
  issue: Issue,
  change: { readonly event: "resolve" | "reopen"; readonly reason: string },
): Promise<Issue> {
  return requestJson({
    method: "POST",
    path: `/issues/${encodeURIComponent(issue.issueId)}/${change.event}`,
    body: jsonBody({ expected_revision: issue.revision, reason: change.reason }),
    decodeResponse: readIssue,
  });
}
